import { StepperSelectionEvent } from "@angular/cdk/stepper";
import {
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgTemplateOutlet,
} from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatStepperModule } from "@angular/material/stepper";
import {
  CertificationType,
  CertificationsService,
  PropertiesService,
  PropertySale,
  PropertyType,
  RealProperty,
  RealPropertyCreate,
  SalesManagementService,
} from "@eemo/api-client";
import { Subscription, catchError, mergeMap, of } from "rxjs";
import { environment } from "src/environments/environment";
import { ModalBaseComponent } from "../base/modal-base/modal-base.component";
import { PropertyAddAddressComponent } from "../property-add-address/property-add-address.component";
import { PropertyAddCertifyComponent } from "../property-add-certify/property-add-certify.component";
import { PropertyAddLocationComponent } from "../property-add-location/property-add-location.component";
import { PropertyAddSaleComponent } from "../property-add-sale/property-add-sale.component";

export type RealPropertyMutable = {
  -readonly [K in keyof RealProperty]: RealProperty[K];
};

export class PropertyAddFullDialogDialogData {
  existingProp?: RealProperty;
}

export type state = "TODO" | "IN_PROGRESS" | "SUCCESS" | "ERROR";

@Component({
  selector: "app-property-add-full-dialog",
  templateUrl: "./property-add-full-dialog.component.html",
  styleUrls: ["./property-add-full-dialog.component.scss"],
  standalone: true,
  imports: [
    PropertyAddSaleComponent,
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    NgIf,
    MatStepperModule,
    PropertyAddAddressComponent,
    MatButtonModule,
    PropertyAddLocationComponent,
    PropertyAddCertifyComponent,
    NgSwitch,
    NgSwitchCase,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
})
export class PropertyAddFullDialogComponent implements OnInit, OnDestroy {
  @ViewChild(PropertyAddAddressComponent)
  addAddress: PropertyAddAddressComponent;
  @ViewChild(PropertyAddLocationComponent)
  addLocation: PropertyAddLocationComponent;
  @ViewChild(PropertyAddSaleComponent) addSale: PropertyAddSaleComponent;
  @ViewChild(PropertyAddCertifyComponent)
  addCertify: PropertyAddCertifyComponent;

  readonly INDEX_ADD_ADDRESS = 0;
  readonly INDEX_ADD_LOCATION = 1;
  readonly INDEX_ADD_SALE = 2;
  readonly INDEX_ADD_CERTIFY = 3;

  dialogTitle: string;

  propertyCreate: RealPropertyCreate;
  propertyUpdate: RealPropertyMutable;
  propertySale: PropertySale;
  existingSaleUid: string;

  submitted = false;
  submitInProgress = false;
  stateCreateProperty: state = "TODO";
  stateUpdateProperty: state = "TODO";
  stateUpdatePropertySale: state = "TODO";
  stateCertifyProperty: state = "TODO";
  managePropertySale = false;

  sub = new Subscription();

  createdPropId: string;
  //selected index is used in steppe to force destroy the component when go back
  selectedIndex = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyAddFullDialogDialogData,
    private propertyService: PropertiesService,
    private certificationService: CertificationsService,
    private saleService: SalesManagementService
  ) {
    this.dialogTitle =
      "PropertyAddFullDialog." +
      (this.data.existingProp ? "titleUpdate" : "titleAdd");
  }

  get addressStepControl(): any {
    return this.addAddress ? this.addAddress.form : { valid: false };
  }

  get mapStepControl(): any {
    return this.addLocation ? this.addLocation.form : { valid: false };
  }

  get certifyStepControl(): any {
    return this.addCertify ? this.addCertify.form : { valid: false };
  }

  get saleStepControl(): any {
    return this.addSale ? this.addSale.form : { valid: false };
  }

  ngOnInit(): void {
    this.propertyUpdate = this.data.existingProp;
    if (this.data.existingProp?.onGoingSelling) {
      this.existingSaleUid = this.data.existingProp.onGoingSelling.uid;
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  stepperChanged(event: StepperSelectionEvent) {
    this.selectedIndex = event.selectedIndex;
    if (event.selectedIndex > event.previouslySelectedIndex) {
      switch (event.previouslySelectedIndex) {
        case this.INDEX_ADD_ADDRESS:
          this.addAddress.onSubmit();
          break;
        case this.INDEX_ADD_LOCATION:
          this.addLocation.onSubmit();
          break;
        case this.INDEX_ADD_SALE:
          this.addSale.onSubmit();
          break;
        case this.INDEX_ADD_CERTIFY:
          this.addCertify.onSubmit();
          break;
      }
    }
  }

  onPropertyCreated(propertyCreate: RealPropertyCreate) {
    this.propertyCreate = propertyCreate;
    this.propertyUpdate = {
      name: propertyCreate.name,
      type: propertyCreate.type,
      address: propertyCreate.address,
      location: propertyCreate.location,
    };
  }

  onPropertyUpdated(propertyUpdate: RealPropertyMutable) {
    this.propertyUpdate = propertyUpdate;
  }

  onPropertySaleUpdated(propertySaleUpdate: PropertySale) {
    this.propertySale = propertySaleUpdate;
  }

  submit() {
    // submit the last stepper to update the property
    this.addCertify.onSubmit();

    if (this.propertyUpdate.deleted) return;

    this.submitted = true;
    this.submitInProgress = true;

    // do we need to manage the property sale ?
    this.managePropertySale =
      this.propertySale != null || this.existingSaleUid != null;

    //create property
    this.stateCreateProperty = "IN_PROGRESS";
    this.sub = (
      this.data.existingProp
        ? of(this.data.existingProp.uid)
        : this.propertyService.createRealProperty(this.propertyCreate)
    )
      .pipe(
        catchError(() => (this.stateCreateProperty = "ERROR")),
        mergeMap((uid: string) => {
          this.createdPropId = uid;
          this.stateCreateProperty = "SUCCESS";
          this.stateUpdateProperty = "IN_PROGRESS";
          return this.propertyService
            .updateRealProperty(this.createdPropId, this.propertyUpdate)
            .pipe(catchError(() => (this.stateUpdateProperty = "ERROR")));
        }),
        mergeMap(() => {
          if (this.propertyUpdate.type !== PropertyType.CONDOMINIUM) {
            return this.propertyService
              .updateRealPropertyAttributes(this.createdPropId, [
                {
                  code: environment.CONDO_STATEMENT_CODE,
                  value: this.propertyUpdate.details?.isInCondo
                    ? "true"
                    : "false",
                },
              ])
              .pipe(catchError(() => (this.stateUpdateProperty = "ERROR")));
          } else {
            return of("");
          }
        }),
        mergeMap(() => {
          this.stateUpdateProperty = "SUCCESS";
          if (!this.managePropertySale) return of("");
          this.stateUpdatePropertySale = "IN_PROGRESS";
          if (this.existingSaleUid) {
            if (this.propertySale) {
              // update sale
              return this.saleService
                .updateSale(this.existingSaleUid, this.propertySale)
                .pipe(
                  catchError(() => (this.stateUpdatePropertySale = "ERROR"))
                );
            } else {
              //remove sale
              return this.saleService
                .deleteSale(this.existingSaleUid)
                .pipe(
                  catchError(() => (this.stateUpdatePropertySale = "ERROR"))
                );
            }
          } else {
            //create sale
            return this.saleService
              .addPropertySale(this.createdPropId, this.propertySale)
              .pipe(catchError(() => (this.stateUpdatePropertySale = "ERROR")));
          }
        }),
        mergeMap(() => {
          this.stateUpdatePropertySale = "SUCCESS";
          if (!this.propertyUpdate.certified) return of("");
          this.stateCertifyProperty = "IN_PROGRESS";
          return this.certificationService
            .certifyRealProperty(this.createdPropId, {
              type: CertificationType.PROFESSIONAL,
              syncOpenData: true,
            })
            .pipe(catchError(() => (this.stateCertifyProperty = "ERROR")));
        })
      )
      .subscribe(() => {
        this.stateCertifyProperty = "SUCCESS";
        this.submitInProgress = false;
      });
  }
}
